import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { blogPosts, formatPostDate } from "./data/blogPosts";
import "./HomePage.css";
import "./TrainingPlanOutput.css";
import { PlanWeekCalendarGrid } from "./TrainingPlanOutput";

function HomeInstallPhoneIcon({ size = 56 }) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="14"
        y="6"
        width="20"
        height="36"
        rx="4"
        stroke="currentColor"
        strokeWidth="2"
      />
      <rect x="20" y="9" width="8" height="2" rx="1" fill="currentColor" />
      <rect x="17" y="16" width="5" height="5" rx="1" fill="currentColor" />
      <rect x="26" y="16" width="5" height="5" rx="1" fill="currentColor" />
      <rect x="17" y="24" width="5" height="5" rx="1" fill="currentColor" />
      <rect x="26" y="24" width="5" height="5" rx="1" fill="currentColor" />
      <circle cx="24" cy="36" r="1.75" fill="currentColor" />
    </svg>
  );
}

/** Sample week for homepage week view preview. */
const HOME_SAMPLE_PLAN_SCHEDULE = {
  Monday: "Tempo Run (5 mi)",
  Tuesday: "Easy Run (4 mi)",
  Wednesday: "Intervals (5 mi total)",
  Thursday: "Rest",
  Friday: "Easy Run (4 mi)",
  Saturday: "Long Run (6 mi)",
  Sunday: "Rest",
};

const HOME_SAMPLE_RACE_DISTANCE = "Half Marathon";
const HOME_SAMPLE_INTENSITY = "Moderate";
const HOME_SAMPLE_WEEK_LABEL = "Week 8: Build";
const HOME_SAMPLE_TOTAL_MILEAGE = "41 mi";
const HOME_SAMPLE_DATE_RANGE = "Jul 6 - 12";

const HOME_SAMPLE_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * November 2026 starts on Sunday. The month grid is Monday-first, matching
 * PlanMonthView: six leading pads, then the 1st in the Sunday column.
 * Four full training weeks run Mon Nov 2–Sun Nov 29. Nov 1 (Sunday) is Rest;
 * Nov 30 (Monday) is outside those four weeks.
 * Each week is Mon–Sun: Tempo, Easy, Speed, Rest, Easy, Long Run, Rest.
 */
const HOME_SAMPLE_MONTH_WEEKS = [
  [
    ["tempo", "Tempo Run", "4 mi"],
    ["easy", "Easy Run", "3 mi"],
    ["interval", "Speed", "2 mi"],
    ["rest", "Rest", null],
    ["easy", "Easy Run", "3 mi"],
    ["longrun", "Long Run", "7 mi"],
    ["rest", "Rest", null],
  ],
  [
    ["tempo", "Tempo Run", "4 mi"],
    ["easy", "Easy Run", "4 mi"],
    ["interval", "Speed", "3 mi"],
    ["rest", "Rest", null],
    ["easy", "Easy Run", "3 mi"],
    ["longrun", "Long Run", "9 mi"],
    ["rest", "Rest", null],
  ],
  [
    ["tempo", "Tempo Run", "5 mi"],
    ["easy", "Easy Run", "4 mi"],
    ["interval", "Speed", "3 mi"],
    ["rest", "Rest", null],
    ["easy", "Easy Run", "4 mi"],
    ["longrun", "Long Run", "11 mi"],
    ["rest", "Rest", null],
  ],
  [
    ["tempo", "Tempo Run", "3 mi"],
    ["easy", "Easy Run", "3 mi"],
    ["interval", "Speed", "2 mi"],
    ["rest", "Rest", null],
    ["easy", "Easy Run", "2 mi"],
    ["longrun", "Long Run", "6 mi"],
    ["rest", "Rest", null],
  ],
];

function HomeSampleMonthDay({ dayNum, workout }) {
  if (!workout) {
    return (
      <div className="pw-month-day pw-month-day--empty" aria-hidden="true">
        <span className="pw-month-day-number">{dayNum}</span>
      </div>
    );
  }

  const [type, label, miles] = workout;
  return (
    <div className={`pw-month-day pw-month-day--${type}`}>
      <span className="pw-month-day-number">{dayNum}</span>
      <span className={`pw-month-day-workout pw-month-day-workout--${type}`}>
        {label}
      </span>
      {miles ? <span className="pw-month-day-miles">{miles}</span> : null}
    </div>
  );
}

function homeSampleMonthCells() {
  const cells = [];
  for (let i = 0; i < 6; i += 1) {
    cells.push({ kind: "pad", key: `pad-start-${i}` });
  }

  cells.push({
    kind: "day",
    key: "day-1",
    dayNum: 1,
    workout: ["rest", "Rest", null],
  });

  HOME_SAMPLE_MONTH_WEEKS.forEach((week, weekIndex) => {
    week.forEach((workout, dayIndex) => {
      const dayNum = 2 + weekIndex * 7 + dayIndex;
      cells.push({
        kind: "day",
        key: `day-${dayNum}`,
        dayNum,
        workout,
      });
    });
  });

  cells.push({ kind: "day", key: "day-30", dayNum: 30, workout: null });

  for (let i = 0; i < 6; i += 1) {
    cells.push({ kind: "pad", key: `pad-end-${i}` });
  }

  return cells;
}

function HomeSampleMonthMock() {
  return (
    <div
      className="pw-month-view home-sample-month-mock"
      aria-label="Sample month view"
    >
      <div className="pw-month-nav">
        <span className="pw-month-nav-btn" aria-hidden="true">
          ‹
        </span>
        <h3 className="pw-month-nav-title">November 2026</h3>
        <span className="pw-month-nav-btn" aria-hidden="true">
          ›
        </span>
      </div>
      <div
        className="pw-month-grid pw-month-grid--weekdays"
        aria-hidden="true"
      >
        {HOME_SAMPLE_WEEKDAYS.map((d) => (
          <div key={d} className="pw-month-weekday">
            {d}
          </div>
        ))}
      </div>
      <div className="pw-month-grid">
        {homeSampleMonthCells().map((cell) =>
          cell.kind === "pad" ? (
            <div
              key={cell.key}
              className="pw-month-day pw-month-day--pad"
              aria-hidden="true"
            />
          ) : (
            <HomeSampleMonthDay
              key={cell.key}
              dayNum={cell.dayNum}
              workout={cell.workout}
            />
          )
        )}
      </div>
      <p className="home-sample-week-caption home-sample-month-caption">
        Sample: {HOME_SAMPLE_RACE_DISTANCE} · {HOME_SAMPLE_INTENSITY}
      </p>
    </div>
  );
}

const latestPaceNotesPost = blogPosts[0];

const heroFadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const sectionScrollFade = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut", delay },
});

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

export default function HomePage() {
  const navigate = useNavigate();
  const [sampleViewMode, setSampleViewMode] = useState("month");

  return (
    <div className="home">
      <section className="home-hero">
        <div className="home-hero-inner">
          <motion.h1
            className="home-headline"
            {...heroFadeUp}
            transition={{ ...heroFadeUp.transition, delay: 0 }}
          >
            Your Race. Your Plan. Your Way.
          </motion.h1>
          <motion.p
            className="home-tagline"
            {...heroFadeUp}
            transition={{ ...heroFadeUp.transition, delay: 0.2 }}
          >
            Build a personalized race training plan in minutes, from 5K to
            Marathon. Simple. Focused. Made for you.
          </motion.p>
          <motion.button
            type="button"
            className="home-cta"
            {...heroFadeUp}
            transition={{ ...heroFadeUp.transition, delay: 0.4 }}
            onClick={() => navigate("/build")}
          >
            Build My Plan
          </motion.button>
        </div>
      </section>

      <section className="home-trust-bar">
        <span className="home-trust-bar-item home-trust-bar-item--highlight">
          334 training plans built
        </span>
        <span className="home-trust-bar-divider" aria-hidden="true" />
        <span className="home-trust-bar-item">No account required</span>
        <span className="home-trust-bar-divider" aria-hidden="true" />
        <span className="home-trust-bar-item">5K to Marathon</span>
      </section>

      <motion.section
        className={`home-sample-showcase home-sample-showcase--${sampleViewMode}`}
        aria-labelledby="home-sample-showcase-heading"
        {...sectionScrollFade(0)}
      >
        <div className="home-sample-showcase-tile">
          <div className="home-sample-showcase-grid">
            <div className="home-sample-showcase-left">
              <h2
                id="home-sample-showcase-heading"
                className="home-sample-showcase-title"
              >
                Inside Your Training Plan
              </h2>
              <p className="home-sample-showcase-sub">
                Every week is clear, structured, and ready to follow. Here is
                what you get in the app.
              </p>
              <ul className="home-sample-showcase-features">
                <li>Month and week views</li>
                <li>Color-coded workouts</li>
                <li>Weekly coaching notes</li>
                <li>Warm-up and cool-down guidance</li>
                <li>Interval prescriptions</li>
                <li>Email your plan summary</li>
              </ul>
            </div>
            <div className="home-sample-showcase-right">
              <div className="home-sample-showcase-controls">
                <div
                  className="pw-plan-view-toggle home-sample-view-toggle"
                  role="group"
                  aria-label="Sample plan layout"
                >
                  <button
                    type="button"
                    className={
                      sampleViewMode === "month" ? "is-active" : undefined
                    }
                    aria-pressed={sampleViewMode === "month"}
                    onClick={() => setSampleViewMode("month")}
                  >
                    Month
                  </button>
                  <button
                    type="button"
                    className={
                      sampleViewMode === "week" ? "is-active" : undefined
                    }
                    aria-pressed={sampleViewMode === "week"}
                    onClick={() => setSampleViewMode("week")}
                  >
                    Week
                  </button>
                </div>
              </div>
              {sampleViewMode === "month" ? (
                <HomeSampleMonthMock />
              ) : (
                <div
                  className="pw-plan-week-card home-sample-week-card"
                  data-pw-plan-section="sample-week"
                >
                  <h3 className="pw-plan-week-title">{HOME_SAMPLE_WEEK_LABEL}</h3>
                  <div className="pw-plan-week-main">
                    <p className="pw-plan-week-mileage">
                      <span className="pw-plan-week-date-range">
                        {HOME_SAMPLE_DATE_RANGE}
                      </span>
                      {" · "}
                      <strong>Total Mileage:</strong> {HOME_SAMPLE_TOTAL_MILEAGE}
                    </p>
                    <p className="home-sample-week-caption">
                      Sample: {HOME_SAMPLE_RACE_DISTANCE} ·{" "}
                      {HOME_SAMPLE_INTENSITY}
                    </p>
                    <PlanWeekCalendarGrid
                      schedule={HOME_SAMPLE_PLAN_SCHEDULE}
                      intensity={HOME_SAMPLE_INTENSITY}
                      raceDistance={HOME_SAMPLE_RACE_DISTANCE}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.section>

      {latestPaceNotesPost && (
        <motion.section
          className="home-latest-pacenotes"
          aria-labelledby="home-latest-pacenotes-heading"
          {...sectionScrollFade(0.1)}
        >
          <div className="home-latest-pacenotes-band">
            <div className="home-latest-pacenotes-grid">
              <div className="home-latest-pacenotes-intro">
                <p className="home-latest-kicker">PACENOTES</p>
                <h2
                  id="home-latest-pacenotes-heading"
                  className="home-latest-heading"
                >
                  Latest from PaceNotes
                </h2>
              </div>
              <div className="home-latest-pacenotes-post">
                <time
                  className="home-latest-date"
                  dateTime={latestPaceNotesPost.date}
                >
                  {formatPostDate(latestPaceNotesPost.date)}
                </time>
                <h3 className="home-latest-post-title">
                  {latestPaceNotesPost.title}
                </h3>
                <p className="home-latest-excerpt">
                  {latestPaceNotesPost.excerpt}
                </p>
                <Link
                  to={`/blog/${latestPaceNotesPost.slug}`}
                  className="home-latest-cta"
                >
                  Read the Post
                </Link>
              </div>
            </div>
          </div>
        </motion.section>
      )}

      <motion.section
        className="home-install-section"
        aria-labelledby="home-install-heading"
        {...sectionScrollFade(0.12)}
      >
        <div className="home-install-inner">
          <div className="home-install-layout">
            <div className="home-install-icon-wrap" aria-hidden="true">
              <HomeInstallPhoneIcon />
            </div>
            <div className="home-install-content">
              <h3 id="home-install-heading">Your plan. Always with you.</h3>
              <p>
                Install PaceWerks on your phone in seconds. No app store. No
                download. Just tap and go.
              </p>
              <div className="home-install-steps">
                <div className="home-install-step">
                  <span className="home-install-step-label">iPhone</span>
                  <span className="home-install-step-instruction">
                    <span className="home-install-step-marker" aria-hidden="true">
                      →
                    </span>
                    Tap Share then Add to Home Screen
                  </span>
                </div>
                <div className="home-install-step">
                  <span className="home-install-step-label">Android</span>
                  <span className="home-install-step-instruction">
                    <span className="home-install-step-marker" aria-hidden="true">
                      →
                    </span>
                    Tap the menu then Add to Home Screen
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="home-features"
        aria-labelledby="home-features-heading"
        {...sectionScrollFade(0.15)}
      >
        <h2 id="home-features-heading" className="home-features-title">
          Why PaceWerks
        </h2>
        <motion.div
          className="home-features-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={cardVariants}>
            <article className="home-feature-card">
              <h3>Plans that fit your race</h3>
              <p>
                From 5K through marathon, with structured blocks, cutbacks, and
                taper aligned to your distance and calendar.
              </p>
            </article>
          </motion.div>
          <motion.div variants={cardVariants}>
            <article className="home-feature-card">
              <h3>Intensity you choose</h3>
              <p>
                Easy through intense: dial volume and quality to match how hard
                you want to train right now.
              </p>
            </article>
          </motion.div>
          <motion.div variants={cardVariants}>
            <article className="home-feature-card">
              <h3>Month and Week Views</h3>
              <p>
                A full monthly calendar shows your entire training at a glance.
                Tap any day to see your workout details and coaching tips.
              </p>
            </article>
          </motion.div>
          <motion.div variants={cardVariants}>
            <article className="home-feature-card">
              <h3>Take It Anywhere</h3>
              <p>
                Get your full training plan delivered to your inbox as a PDF.
                Save it, print it, or keep it on your phone. Your plan goes
                where you go.
              </p>
            </article>
          </motion.div>
        </motion.div>
      </motion.section>

      <motion.section
        className="home-bottom-cta"
        aria-labelledby="home-bottom-cta-heading"
        {...sectionScrollFade(0.2)}
      >
        <h2 id="home-bottom-cta-heading" className="home-bottom-cta-title">
          Ready to run smarter?
        </h2>
        <button
          type="button"
          className="home-cta"
          onClick={() => navigate("/build")}
        >
          Build My Plan
        </button>
      </motion.section>
    </div>
  );
}
